import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useToast from "../../../helpers/useToast";
import useEncryption from "../../../services/useEncryption";
import { addDisplayDataApi } from "../../../api/userApi/userApi";
import { createAuditLogs } from "../../../api/providerApi/providerApi";
import { useSelector } from "react-redux";
import useHeaders from "../../../helpers/useHeaders";

const UpdateAboutModal = ({
  open,
  handleClose,
  aboutMe,
  userId,
  setUpdate,
  doctorData,
}) => {
  const { encryptData, decryptData } = useEncryption();
  const { id } = useParams();
  const toastOption = useToast();
  const decryptedId = decryptData(decodeURIComponent(id));
  const [loading, setLoading] = useOutletContext();
  const headers = useHeaders();
  const { role } = useSelector((state) => state.auth);

  const formik = useFormik({
    initialValues: {
      about_me: aboutMe || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      about_me: Yup.string().required("About me is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      const formData = new FormData();

      // Fill only the required fields for about update, keep others from doctorData
      formData.append("firstName", doctorData?.firstName || "");
      formData.append("lastName", doctorData?.lastName || "");
      formData.append("streetAddress", doctorData?.streetAddress || "");
      formData.append("city", doctorData?.city || "");
      formData.append("state", doctorData?.state || "");
      formData.append("country", doctorData?.country || "");
      formData.append("zipCode", doctorData?.zipCode || "");
      formData.append("aboutMe", values.about_me);
      formData.append("organizationId", userId);
      formData.append(
        "insurance_details",
        JSON.stringify(doctorData?.insurance_details || [])
      );
      if (doctorData?.photo) {
        formData.append("profileImg", doctorData.photo);
      }

      try {
        let response = await addDisplayDataApi(formData);

        setLoading(false);
        handleClose();
        resetForm();
        setUpdate((prev) => !prev);

        let obj = {
          event: `${role} has updated About Me section.`,
        };
        await createAuditLogs({ data: encryptData(obj) }, headers);

        toast.success(response.data.message, toastOption);
      } catch (error) {
        setLoading(false);
        if (error.response) {
          toast.error(
            error.response.data.message || "An error occurred.",
            toastOption
          );
        } else {
          toast.error("Network error. Please try again.", toastOption);
        }
      }
    },
  });

  return (
    <Dialog open={open} onClose={handleClose}>
      <div className="d-flex align-items-center justify-content-between">
        <DialogTitle>Edit About Section</DialogTitle>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent dividers>
        <form onSubmit={formik.handleSubmit}>
          <TextField
            label="About me"
            className="w-100"
            variant="outlined"
            size="small"
            name="about_me"
            multiline
            rows={4}
            value={formik.values.about_me}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.about_me && Boolean(formik.errors.about_me)}
            helperText={formik.touched.about_me && formik.errors.about_me}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={formik.handleSubmit} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateAboutModal;